/**
 * resolveSharedItems — the executable specification of Royale's live-sharing
 * rule (SIGNUP D2; LOCKED by Mark 2026-10-13 after a two-agent stress test —
 * docs/current/signup-sharing.md § Decisions, copied verbatim there).
 *
 * ONE calculation, a pure function of the CURRENT stored state — so the result
 * is order-independent by construction (the rule's "order of events never
 * changes the result" falls out for free: nothing here reads a history):
 *
 *   For each (viewer, holder, item):
 *     visible = the holder's personal setting for this viewer if one exists
 *               ('on' / 'off'); otherwise ON iff the holder shares their Full
 *               profile with this viewer (049), or ANY group the viewer is in
 *               shares the item.
 *   Personal 'off' always beats groups. Union across groups. The name form
 *   resolves to the FULLER form across the viewer's groups; a name never hides.
 *
 * WHERE THIS ACTUALLY SHIPS: under the open-access lockdown (042) a viewer
 * cannot read a holder's details or group memberships except through a
 * my_ids()-gated SECURITY DEFINER function, so the shipping resolver is the SQL
 * definer function (migration 046, plan step 3) that extends
 * get_connection_profiles. This module is that function's executable spec and
 * test oracle: the stress scenarios run against it as fast unit tests (AC #11),
 * and the SQL mirrors this logic exactly (spot-checked against dev). It is
 * standalone — wired into no live path — so it is dormant by construction until
 * the data layer lands behind NEW_EXPERIENCE.
 *
 * item_key is the contact SLOT key (`phone`, `phone2`, `email`, `email2`, …;
 * the fixed ALL_CONTACT_KEYS set) or `livesIn` / `aboutMe` / `nameForm` — the
 * approved field-key shape (Mark 2026-10-14). Slot keys are already stable, so
 * a holder with two emails has the `email` + `email2` items (one switch per
 * slot, not per type). Only slots the holder CURRENTLY has resolve — a deleted
 * detail simply isn't in holderDetails; a re-added detail reuses its slot and
 * starts unshared because applyContactDeletion cleared that slot's rows on
 * delete (rules 11, 12 — the clear lives there, not here). The pseudo-items
 * 'livesIn' and 'aboutMe' are ordinary on/off items; the name form is resolved
 * separately (a choice of form, not a visibility toggle). The resolver is
 * key-agnostic — it compares strings — so it is unaffected by the key scheme.
 */

/** Follow-the-groups: the default, stored as nothing. */
const FOLLOW = null;

/** The reserved per-person item for `Full profile` (migration 049). Never a detail. */
export const FULL_PROFILE_ITEM = 'fullProfile';

/**
 * @param {object} state
 * @param {Array<{itemKey:string, type?:string, value?:*}>} [state.holderDetails]
 *   The holder's current items by stable id, plus { itemKey:'livesIn' } and
 *   { itemKey:'aboutMe' } when present.
 * @param {{first?:string, full?:string}} [state.name] The holder's name forms.
 * @param {string[]} [state.viewerGroupIds] Groups the viewer belongs to.
 * @param {Object<string,{items?:string[], nameForm?:('first'|'full')}>} [state.groupShareSets]
 *   Per group: the item keys it shares, and its chosen name form.
 * @param {Object<string,('on'|'off')>} [state.personalSettings]
 *   Sparse per-item overrides for THIS viewer; an absent key = follow groups.
 * @returns {{items:Array<{itemKey:string,type?:string,value:*,visible:boolean,personalState:('on'|'off'|null),sourceGroupIds:string[]}>, name:{form:('first'|'full'),value:string,conflict:boolean}}}
 *
 * The viewer-facing read path sends only `items.filter(i => i.visible)` and
 * their values; the holder's own sheet uses the full list (including off items)
 * to render toggles. The SQL definer function returns values for visible items
 * only — a hidden value never crosses to the viewer.
 */
export const resolveSharedItems = ({
  holderDetails = [],
  name = {},
  viewerGroupIds = [],
  groupShareSets = {},
  personalSettings = {},
} = {}) => {
  // Groups the viewer is in that share a given item (also the item's "source"
  // for the sheet label, independent of any personal override).
  const groupsSharing = (itemKey) =>
    viewerGroupIds.filter((g) => groupShareSets[g]?.items?.includes(itemKey));

  // Full profile (migration 049): a subscription to everything the holder has,
  // including what they add later — a reserved per-person row, 'fullProfile' =
  // 'on'. A personal 'off' still beats it, like it beats a group.
  const fullProfile = personalSettings[FULL_PROFILE_ITEM] === 'on';

  const items = holderDetails.map((detail) => {
    const personal = personalSettings[detail.itemKey] ?? FOLLOW;
    const sourceGroupIds = groupsSharing(detail.itemKey);
    const visible =
      personal === 'on'
        ? true
        : personal === 'off'
          ? false
          : fullProfile || sourceGroupIds.length > 0;
    return { ...detail, visible, personalState: personal, sourceGroupIds };
  });

  return { items, name: resolveNameForm(name, viewerGroupIds, groupShareSets) };
};

/**
 * Fuller form wins across the viewer's groups; default 'first'; a name never
 * hides. `conflict` flags disagreement so the person's sheet can note it
 * (rule 9).
 */
const resolveNameForm = (name, viewerGroupIds, groupShareSets) => {
  const forms = viewerGroupIds.map((g) => groupShareSets[g]?.nameForm).filter(Boolean);
  const form = forms.includes('full') ? 'full' : 'first';
  const conflict = forms.includes('full') && forms.includes('first');
  const value = form === 'full' ? (name.full ?? name.first ?? '') : (name.first ?? '');
  return { form, value, conflict };
};
